import type {
  DeveloperGuideCodeSample,
  DeveloperGuideSection,
} from "../types";

const SECTION_BLOCK_RE =
  /```developer-guide-section\s*\n([\s\S]*?)```/g;
const CODE_SAMPLES_BLOCK_RE = /```code-samples\s*\n([\s\S]*?)```/g;

export interface ParsedSectionEnhancement {
  sectionId?: string;
  title?: string;
  instructions?: string;
}

export function parseSectionEnhancements(
  response: string,
): ParsedSectionEnhancement[] {
  const results: ParsedSectionEnhancement[] = [];
  for (const match of response.matchAll(SECTION_BLOCK_RE)) {
    try {
      const parsed = JSON.parse(match[1].trim()) as ParsedSectionEnhancement;
      results.push(parsed);
    } catch {
      /* skip invalid block */
    }
  }
  return results;
}

export function parseCodeSampleEnhancements(
  response: string,
): DeveloperGuideCodeSample[] {
  const results: DeveloperGuideCodeSample[] = [];
  for (const match of response.matchAll(CODE_SAMPLES_BLOCK_RE)) {
    try {
      const parsed = JSON.parse(match[1].trim()) as
        | DeveloperGuideCodeSample[]
        | DeveloperGuideCodeSample;
      const items = Array.isArray(parsed) ? parsed : [parsed];
      for (const item of items) {
        results.push({
          id: item.id || crypto.randomUUID(),
          language: item.language || "text",
          label: item.label || "",
          code: item.code || "",
        });
      }
    } catch {
      /* skip invalid block */
    }
  }
  return results;
}

export function applySectionEnhancement(
  sections: DeveloperGuideSection[],
  enhancement: ParsedSectionEnhancement,
): DeveloperGuideSection[] {
  if (!enhancement.sectionId) return sections;
  return sections.map((section) => {
    if (section.id !== enhancement.sectionId) return section;
    return {
      ...section,
      title: enhancement.title ?? section.title,
      instructions: enhancement.instructions ?? section.instructions,
    };
  });
}

export function applyCodeSamplesToSection(
  sections: DeveloperGuideSection[],
  sectionId: string,
  samples: DeveloperGuideCodeSample[],
): DeveloperGuideSection[] {
  const forSection = samples.filter(
    (s) => !(s as DeveloperGuideCodeSample & { sectionId?: string }).sectionId
      || (s as DeveloperGuideCodeSample & { sectionId?: string }).sectionId === sectionId,
  );
  if (forSection.length === 0) return sections;
  return sections.map((section) =>
    section.id === sectionId
      ? { ...section, codeSamples: forSection }
      : section,
  );
}
